import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Sparkles, Calendar } from 'lucide-react';
import { useSiteContent } from '@/hooks/useSiteContent';
import { trackEvent } from '@/lib/analytics';

const SEEN_KEY = 'dubinko_exit_seen';

/**
 * Exit-intent promo popup. Appears once per browsing session when a promo is active:
 *  - Desktop: when the cursor leaves through the top of the viewport.
 *  - Mobile: after 30s on the page (no reliable exit-intent on touch).
 * Hidden on /admin and while inside the booking wizard.
 */
export default function ExitIntentPopup() {
  const { content } = useSiteContent();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hidden = location.pathname.startsWith('/admin') || location.pathname.startsWith('/rezervacija');
  const active = content.promo_active && content.promo_code && !hidden;

  useEffect(() => {
    if (!active) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;

    let mobileTimer;
    const trigger = () => {
      if (sessionStorage.getItem(SEEN_KEY)) return;
      sessionStorage.setItem(SEEN_KEY, '1');
      setOpen(true);
      trackEvent('exit_popup_shown');
    };

    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    const isTouch = window.matchMedia('(max-width: 768px)').matches;
    if (isTouch) {
      mobileTimer = setTimeout(trigger, 30000);
    } else {
      document.addEventListener('mouseout', onMouseOut);
    }
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [active]);

  if (!open) return null;

  const goBooking = () => {
    trackEvent('exit_popup_cta', { code: content.promo_code });
    setOpen(false);
    navigate(`/rezervacija?promo=${encodeURIComponent(content.promo_code)}`);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="exit-intent-popup" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl bg-white exit-pop">
        <div className="p-8 text-white text-center relative"
          style={{ background: 'linear-gradient(135deg, #059669 0%, #023E8A 50%, #5B21B6 100%)' }}>
          <button onClick={() => setOpen(false)} data-testid="exit-popup-close"
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20" aria-label="Zatvori">
            <X className="w-5 h-5" />
          </button>
          <Sparkles className="w-10 h-10 mx-auto" />
          <h3 className="font-display text-3xl font-extrabold mt-3">Sačekajte! 🎁</h3>
          <p className="mt-2 text-white/90">
            {content.promo_banner_text || `Iskoristite popust uz kod ${content.promo_code}`}
          </p>
        </div>
        <div className="p-8 text-center">
          <p className="text-slate-600">Vaš promo kod:</p>
          <div className="mt-2 inline-block px-6 py-3 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-300">
            <span className="font-display text-2xl font-extrabold tracking-widest text-slate-900"
              data-testid="exit-popup-code">{content.promo_code}</span>
          </div>
          <button onClick={goBooking} data-testid="exit-popup-book"
            className="btn-primary w-full mt-6 px-6 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" /> Zakaži uz popust
          </button>
          <button onClick={() => setOpen(false)} data-testid="exit-popup-dismiss"
            className="mt-3 text-sm text-slate-500 hover:text-slate-700">Ne, hvala</button>
        </div>
      </div>
    </div>
  );
}
